import { useMemo } from "react";
import {
  ActivityIndicator,
  Pressable,
  RefreshControl,
  SectionList,
  Text,
  View,
  type SectionListRenderItem,
} from "react-native";
import { useTranslation } from "react-i18next";
import {
  buildMonthSections,
  coerceLocale,
  type Session,
  type WorkoutMonthSection,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { makeStyles } from "./workoutsStyles";
import { WorkoutListRow, MonthSectionHeader } from "./WorkoutRows";

// THE Workouts history surface — month-sectioned list of performed sessions,
// one tree for mobile's Workouts tab and the web-rnw console. Presentational +
// self-contained for the LIST: it owns the section build, loading/error/empty
// states and the load-older/end-of-list footer. The host injects the data
// (items + pagination + refresh seam from useSessionsListData on mobile, a
// paginated fetch + SWR cache on web), the collapse state, and the row
// interactions. Mobile wires tap → edit-in-logger, ↻ repeat, long-press
// delete + the surrounding logger/outbox chrome; the web console wires tap →
// select (master-detail) and omits the repeat/delete affordances.

export type WorkoutsViewProps = {
  theme: UiTheme;
  /** Loaded sessions; null = first page still loading. */
  items: Session[] | null;
  /** Load error message (already localized by the host); null = none. */
  error: string | null;
  refreshing: boolean;
  loadingMore: boolean;
  /** Pagination cursor; non-null = older pages remain. */
  cursor: string | null;
  collapsedMonths: ReadonlySet<string>;
  onToggleMonth: (key: string) => void;
  onRefresh: () => void;
  onEndReached: () => void;
  /** Row tap (mobile → open edit in logger; web → select for detail). */
  onSelectSession: (id: string) => void;
  /**
   * Whether rows respond to tap. Defaults true (mobile). The web console
   * passes false until the master-detail pane lands (S5) so a row is never a
   * button that does nothing.
   */
  selectable?: boolean;
  /** Highlighted row in the web master-detail; ignored on mobile. */
  selectedId?: string | null;
  /** Repeat affordance (mobile only). */
  onRepeat?: (id: string) => void;
  /** Long-press delete (mobile only). */
  onDelete?: (item: Session) => void;
  openingId?: string | null;
  repeatingId?: string | null;
  deletingId?: string | null;
  /** Pull-to-refresh control (mobile yes; web console can omit). */
  enableRefreshControl?: boolean;
  /** Inside a parent ScrollView (the coach's athlete page, #25): the list
   *  does not scroll itself, and older pages load only from the footer tap,
   *  since a list that never scrolls would read its end as reached at once. */
  nested?: boolean;
};

export function WorkoutsView({
  theme,
  items,
  error,
  refreshing,
  loadingMore,
  cursor,
  collapsedMonths,
  onToggleMonth,
  onRefresh,
  onEndReached,
  onSelectSession,
  selectable = true,
  selectedId,
  onRepeat,
  onDelete,
  openingId,
  repeatingId,
  deletingId,
  enableRefreshControl = true,
  nested = false,
}: WorkoutsViewProps) {
  const { t, i18n } = useTranslation();
  const locale = coerceLocale(i18n.language);
  const styles = makeStyles(theme);

  const sections = useMemo(
    () => (items ? buildMonthSections(items, collapsedMonths) : []),
    [items, collapsedMonths],
  );

  if (items === null && error === null) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={theme.palette.ink2} />
      </View>
    );
  }
  if (error !== null && (items === null || items.length === 0)) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>{error}</Text>
      </View>
    );
  }
  if (items !== null && items.length === 0) {
    return (
      <View style={styles.center}>
        <Text style={styles.emptyText}>{t("mobile.workouts.empty")}</Text>
      </View>
    );
  }

  const renderItem: SectionListRenderItem<Session, WorkoutMonthSection> = ({
    item,
  }) => (
    <WorkoutListRow
      item={item}
      selected={selectedId === item.id}
      selectable={selectable}
      opening={openingId === item.id}
      repeating={repeatingId === item.id}
      deleting={deletingId === item.id}
      onSelect={onSelectSession}
      onRepeat={onRepeat}
      onDelete={onDelete}
      styles={styles}
      theme={theme}
      t={t}
      locale={locale}
    />
  );

  return (
    <SectionList
      style={nested ? undefined : styles.flex}
      scrollEnabled={!nested}
      sections={sections}
      keyExtractor={(s) => s.id}
      renderItem={renderItem}
      renderSectionHeader={({ section }) => (
        <MonthSectionHeader
          section={section}
          onToggle={onToggleMonth}
          styles={styles}
          locale={locale}
        />
      )}
      stickySectionHeadersEnabled={false}
      ItemSeparatorComponent={() => <View style={styles.separator} />}
      refreshControl={
        enableRefreshControl && !nested ? (
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={theme.palette.brand500}
          />
        ) : undefined
      }
      onEndReached={nested ? undefined : onEndReached}
      onEndReachedThreshold={0.5}
      ListFooterComponent={
        loadingMore ? (
          <View style={styles.footer}>
            <ActivityIndicator color={theme.palette.ink3} />
          </View>
        ) : cursor !== null ? (
          // Older pages exist. onEndReached can't be relied on when the list
          // is too short to scroll (collapsed months leave the viewport
          // unfilled), so always offer an explicit tap to page back — same
          // load path, just user-triggered.
          <Pressable
            accessibilityRole="button"
            onPress={onEndReached}
            style={({ pressed }) => [
              styles.loadOlder,
              pressed && styles.loadOlderPressed,
            ]}
          >
            <Text style={styles.loadOlderText}>
              {t("mobile.workouts.loadOlder")}
            </Text>
          </Pressable>
        ) : items !== null && items.length > 0 ? (
          <View style={styles.footer}>
            <Text style={styles.footerText}>
              {t("mobile.workouts.endOfList", { count: items.length })}
            </Text>
          </View>
        ) : null
      }
    />
  );
}
